import { useEffect, useState } from "react";
import { Save, Plus, Trash2, Loader2, Eye, EyeOff, Star, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { RichTextEditor } from "@/components/RichText";

const input = "w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]";
const uid = () => crypto.randomUUID();

export function OrbitManager() {
  const [o, setO] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/orbit").then((r) => setO({
      published: false, logo: "", hero_image: "", coming_soon_image: "", headline: "", tagline: "Precision Chalk",
      sub_tagline: "Master Every Motion", intro: "", cta_label: "", cta_url: "",
      gallery: [], specs: [], reviews: [], ...r.data,
    })).catch(() => {});
  }, []);

  if (!o) return <p className="text-[#555555]">Loading…</p>;

  const set = (k, v) => setO((s) => ({ ...s, [k]: v }));
  const setList = (key, i, k, v) => set(key, o[key].map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const addTo = (key, item) => set(key, [...(o[key] || []), item]);
  const removeFrom = (key, i) => set(key, o[key].filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/orbit", o);
      setO((s) => ({ ...s, ...data }));
      toast.success("ORBIT page saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="orbit-manager">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading font-bold text-2xl">ORBIT Precision Chalk</h2>
          <p className="text-sm text-[#555555]">Build the product page. It stays private until you publish it.</p>
        </div>
        <div className="flex items-center gap-3">
          <a href="/orbit" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#1E63C4] font-semibold" data-testid="orbit-view-link">
            <ExternalLink className="h-4 w-4" /> View page
          </a>
          <button onClick={save} disabled={busy} data-testid="orbit-save-btn" className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-5 py-2.5 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </button>
        </div>
      </div>

      {/* Publish toggle */}
      <button
        onClick={() => set("published", !o.published)}
        data-testid="orbit-publish-toggle"
        className={`w-full flex items-center gap-3 rounded-lg border p-4 text-left transition-colors ${o.published ? "border-green-300 bg-green-50" : "border-[#E5E7EB] bg-[#F9FAF8]"}`}
      >
        {o.published ? <Eye className="h-6 w-6 text-green-600" /> : <EyeOff className="h-6 w-6 text-[#999]" />}
        <div>
          <p className="font-semibold">{o.published ? "Published — live to the public" : "Private — hidden from the public"}</p>
          <p className="text-xs text-[#555]">{o.published ? "The page is visible at /orbit and appears in the menu." : "Only you (logged in) can preview it. It shows “Coming soon” to everyone else and is hidden from the menu."} Click to {o.published ? "unpublish" : "publish"}. Remember to Save.</p>
        </div>
      </button>

      {/* Coming soon / teaser image */}
      <section className="rounded-lg border border-[#E5E7EB] p-5 space-y-3">
        <h3 className="font-heading font-bold text-lg">Coming-soon / teaser image</h3>
        <p className="text-xs text-[#555555]">Shown on the public <strong>/orbit</strong> page while it&apos;s unpublished. Upload your teaser poster here — it replaces the default &quot;Coming soon&quot; text.</p>
        <ImageUpload label="" value={o.coming_soon_image} onChange={(v) => set("coming_soon_image", v)} testid="orbit-comingsoon-upload" />
      </section>

      {/* Hero */}
      <section className="rounded-lg border border-[#E5E7EB] p-5 space-y-4">
        <h3 className="font-heading font-bold text-lg">Hero</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <ImageUpload label="Logo (optional — uses the ORBIT wordmark if empty)" value={o.logo} onChange={(v) => set("logo", v)} testid="orbit-logo-upload" />
          <ImageUpload label="Hero product image" value={o.hero_image} onChange={(v) => set("hero_image", v)} testid="orbit-hero-upload" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="block text-xs font-medium text-[#555] mb-1">Tagline</label><input className={input} value={o.tagline} onChange={(e) => set("tagline", e.target.value)} data-testid="orbit-tagline" /></div>
          <div><label className="block text-xs font-medium text-[#555] mb-1">Sub-tagline</label><input className={input} value={o.sub_tagline} onChange={(e) => set("sub_tagline", e.target.value)} data-testid="orbit-subtagline" /></div>
        </div>
        <div><label className="block text-xs font-medium text-[#555] mb-1">Headline</label><input className={input} value={o.headline} onChange={(e) => set("headline", e.target.value)} placeholder="Motion, shaped into control." data-testid="orbit-headline" /></div>
        <div>
          <label className="block text-xs font-medium text-[#555] mb-1">Intro / description</label>
          <RichTextEditor value={o.intro} onChange={(v) => set("intro", v)} testid="orbit-intro-editor" placeholder="Describe the chalk — type or paste formatted text" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="block text-xs font-medium text-[#555] mb-1">Button label (optional)</label><input className={input} value={o.cta_label} onChange={(e) => set("cta_label", e.target.value)} placeholder="Learn more" data-testid="orbit-cta-label" /></div>
          <div><label className="block text-xs font-medium text-[#555] mb-1">Button link URL (optional)</label><input className={input} value={o.cta_url} onChange={(e) => set("cta_url", e.target.value)} placeholder="https://…" data-testid="orbit-cta-url" /></div>
        </div>
      </section>

      {/* Specs */}
      <section className="rounded-lg border border-[#E5E7EB] p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg">Specifications</h3>
          <button onClick={() => addTo("specs", { id: uid(), label: "", value: "" })} data-testid="orbit-add-spec" className="inline-flex items-center gap-1 text-sm text-[#1E63C4] font-semibold"><Plus className="h-4 w-4" /> Add spec</button>
        </div>
        {(o.specs || []).map((s, i) => (
          <div key={s.id} className="flex gap-2" data-testid={`orbit-spec-row-${i}`}>
            <input className={input} placeholder="Label (e.g. Weight)" value={s.label} onChange={(e) => setList("specs", i, "label", e.target.value)} data-testid={`orbit-spec-label-${i}`} />
            <input className={input} placeholder="Value (e.g. 12 g)" value={s.value} onChange={(e) => setList("specs", i, "value", e.target.value)} data-testid={`orbit-spec-value-${i}`} />
            <button onClick={() => removeFrom("specs", i)} className="text-red-500 px-2" data-testid={`orbit-spec-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </section>

      {/* Gallery */}
      <section className="rounded-lg border border-[#E5E7EB] p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg">Gallery</h3>
          <button onClick={() => addTo("gallery", { id: uid(), image: "", caption: "" })} data-testid="orbit-add-gallery" className="inline-flex items-center gap-1 text-sm text-[#1E63C4] font-semibold"><Plus className="h-4 w-4" /> Add picture</button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {(o.gallery || []).map((g, i) => (
            <div key={g.id} className="border border-[#E5E7EB] rounded-md p-3 space-y-2" data-testid={`orbit-gallery-row-${i}`}>
              <ImageUpload label="" value={g.image} onChange={(v) => setList("gallery", i, "image", v)} testid={`orbit-gallery-image-${i}`} />
              <input className={input} placeholder="Caption (optional)" value={g.caption} onChange={(e) => setList("gallery", i, "caption", e.target.value)} data-testid={`orbit-gallery-caption-${i}`} />
              <button onClick={() => removeFrom("gallery", i)} className="inline-flex items-center gap-1 text-xs text-red-500 font-semibold" data-testid={`orbit-gallery-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /> Remove</button>
            </div>
          ))}
        </div>
      </section>

      {/* Reviews */}
      <section className="rounded-lg border border-[#E5E7EB] p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg">Reviews / testimonials</h3>
          <button onClick={() => addTo("reviews", { id: uid(), author: "", location: "", rating: 5, quote: "", date: "" })} data-testid="orbit-add-review" className="inline-flex items-center gap-1 text-sm text-[#1E63C4] font-semibold"><Plus className="h-4 w-4" /> Add review</button>
        </div>
        {(o.reviews || []).map((r, i) => (
          <div key={r.id} className="border border-[#E5E7EB] rounded-md p-3 space-y-2" data-testid={`orbit-review-row-${i}`}>
            <div className="grid sm:grid-cols-3 gap-2">
              <input className={input} placeholder="Author" value={r.author} onChange={(e) => setList("reviews", i, "author", e.target.value)} data-testid={`orbit-review-author-${i}`} />
              <input className={input} placeholder="Location (optional)" value={r.location} onChange={(e) => setList("reviews", i, "location", e.target.value)} data-testid={`orbit-review-location-${i}`} />
              <div className="flex items-center gap-1" data-testid={`orbit-review-rating-${i}`}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setList("reviews", i, "rating", n)} className="p-0.5" aria-label={`${n} stars`}>
                    <Star className="h-5 w-5" style={{ color: n <= (r.rating || 0) ? "#D2302B" : "#ccc", fill: n <= (r.rating || 0) ? "#D2302B" : "transparent" }} />
                  </button>
                ))}
              </div>
            </div>
            <RichTextEditor key={r.id} value={r.quote} onChange={(v) => setList("reviews", i, "quote", v)} testid={`orbit-review-quote-${i}`} placeholder="Review text — type or paste" />
            <button onClick={() => removeFrom("reviews", i)} className="inline-flex items-center gap-1 text-xs text-red-500 font-semibold" data-testid={`orbit-review-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /> Remove review</button>
          </div>
        ))}
      </section>

      <div className="flex justify-end">
        <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-5 py-2.5 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </button>
      </div>
    </div>
  );
}
